import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'
import { Icon } from '../icons'
import type { TableColumn } from './viewTypes'
import type { TableLayoutState } from './useTableLayout'
import type { ViewportTier } from './viewport'

// AB#3953 — the ONE column-configuration surface every shared table primitive renders (ListView,
// LineItemsSlot, DataTableSlot, JsonTableSlot). A cog at the table's top-right opens configuration
// mode: a panel lists the SHOWN columns with Left / Right / Hide controls and the AVAILABLE (hidden)
// columns with a position choice and Add; every chip and every table heading is also a drag source,
// and every heading / shown chip a drop target (the panel itself is the drop target that hides). Drag
// uses Pointer Events only — no library — and every drag has a button equivalent, so keyboard and
// assistive-technology users get the whole feature, not a subset. Each change re-lays the table out
// at once, is announced through an aria-live region, and is persisted by the hook behind it
// (useTableLayout); the configurator itself owns no layout state.
//
// AB#3986 — the panel is an OVERLAY, never part of the table's layout. It used to be a flex sibling
// of the table, so a short table grew to the panel's height and the list became a second vertical
// scroll region beside the page's. Two hosts wrap their table in an `overflow-hidden` card and every
// host in an `overflow-x: auto` frame, so an absolutely positioned child would be clipped: the panel
// is `position: fixed`, in the frame's own DOM (not a portal — a portal escapes Modal's focus trap
// and its z-index band), anchored to the frame's top-right, re-placed on scroll / resize, kept inside
// the frame's scroll parent, its height bounded so it scrolls within itself. The table's box and the
// page's scroll height are identical open or closed. The compact tier keeps the panel in flow below
// the cards, unbounded, so a phone still has exactly one scroll region: the page.
//
// AB#3987 — a drag paints a ghost chip that follows the pointer, moved imperatively inside
// requestAnimationFrame (no React render per pointermove); the drop target resolves against rects
// cached when the drag starts (refreshed on scroll / resize), the marker state changes only when the
// target does, and the insertion marker is an INSET box-shadow, so no column changes width under the
// pointer. The lifted source dims. Nothing animates, so reduced motion needs no special case.
//
// AB#3988 — Escape is a document-level capture listener while the panel is open, so it works with
// focus on the cog, on the body after a click in the table, or anywhere else: the first Escape
// cancels a live drag, the next closes the panel and returns focus to the cog; it stops propagation,
// so a parent dialog never closes with it. Opening moves focus into the panel. Escape on a focused
// position <select> is left to the select (an open native dropdown closes on it, and nothing tells
// open from closed) — Tab away or press Done.
//
// Idle (cog not pressed) the primitives' markup is untouched: `frame` returns the table as given
// and `header` decorates nothing, so a table without an identity, or with fewer than two columns
// to choose between, renders byte-for-byte what it rendered before this existed.

export interface ColumnConfiguratorOptions {
    /** The table identity (columnLayout.tableIdentity). Undefined = no cog at all. */
    identity: string | undefined
    layout: TableLayoutState
    viewport?: ViewportTier | undefined
    /** Human label for a field: column label → field meta label → field name. */
    labelOf: (field: string) => string
}

/** Spread onto a heading cell while configuring: the drop-target markers a drag resolves against, plus the insertion marker and lifted state as data attributes. */
export interface ColumnHeaderProps {
    'data-column-drop': 'header'
    'data-column-index': number
    'data-column-marker'?: 'before' | 'after'
    'data-column-lifted'?: 'true'
}

export interface ColumnHeaderDecoration {
    props: ColumnHeaderProps | Record<string, never>
    /** Extra classes: the insertion marker while a drag hovers this heading (an inset shadow, never a border — a border shifts the columns), dimming while it is dragged. */
    className: string
    /** The drag grip to render inside the heading while configuring (null when idle). */
    grip: ReactNode
}

export interface ColumnConfiguratorHandle {
    /** True when the cog exists: an identity and at least two columns to choose between. */
    enabled: boolean
    /** True while configuration mode is open. */
    active: boolean
    /** The cog button (null when not enabled). Render it at the table's top-right. */
    cog: ReactNode
    /** Wraps the table with the configuration panel while active; returns `table` unchanged otherwise. */
    frame: (table: ReactNode) => ReactNode
    /** Per visible heading (by effective index) — drop-target props, marker classes and the drag grip. */
    header: (field: string, index: number) => ColumnHeaderDecoration
}

type DragSource = { field: string; from: 'visible' | 'hidden' }
/** An insertion index over the visible list, or the panel (= hide). */
type DropTarget = number | 'panel' | null
type Box = { left: number; top: number; right: number; bottom: number }
/** A drop target's cached rect; `axis` is where its midpoint splits before / after (headings run across, chips run down). */
type DropZone = { target: 'panel' | number; axis: 'x' | 'y'; box: Box }

const IDLE_HEADER: ColumnHeaderDecoration = { props: {}, className: '', grip: null }
const END = 'end'
/** The panel's width (w-72) and the gutter it keeps from the edge of whatever bounds it. */
const PANEL_WIDTH = 288
const GUTTER = 8
const MIN_PANEL_HEIGHT = 160
const MIN_PANEL_WIDTH = 200
/** A scroll parent shorter than this (a widget body) bounds nothing useful: the viewport bounds instead. */
const MIN_BOUND_HEIGHT = 240
/** The ghost sits down-right of the pointer, so the pointer never covers what it says. */
const GHOST_OFFSET = 12
/** A field name inside a quoted attribute selector (CSS.escape is absent in jsdom). */
const chipSelector = (field: string, action: string) => `[data-column-chip="${field.replace(/["\\]/g, '\\$&')}"] [data-action="${action}"]`

const smallButton = 'rounded border border-neutral-300 dark:border-slate-600 px-1.5 py-0.5 text-[11px] font-medium text-neutral-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-neutral-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
const gripButton = 'cursor-grab touch-none select-none rounded px-0.5 text-neutral-400 hover:text-neutral-700 dark:text-slate-500 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
// Insertion markers are INSET box-shadows, never borders: a border widens the cell and shifts every
// column while the pointer is over it, flipping the target back and forth (AB#3987). Tailwind
// blue-500 / blue-400, the colours the AB#3953 borders used, spelled as hex because a consumer's CSS
// only carries the theme variables its own classes reference.
const MARK = {
    before: 'shadow-[inset_2px_0_0_0_#3b82f6] dark:shadow-[inset_2px_0_0_0_#60a5fa]',
    after: 'shadow-[inset_-2px_0_0_0_#3b82f6] dark:shadow-[inset_-2px_0_0_0_#60a5fa]',
    above: 'shadow-[inset_0_2px_0_0_#3b82f6] dark:shadow-[inset_0_2px_0_0_#60a5fa]',
    below: 'shadow-[inset_0_-2px_0_0_#3b82f6] dark:shadow-[inset_0_-2px_0_0_#60a5fa]',
} as const

const toBox = (r: DOMRect): Box => ({ left: r.left, top: r.top, right: r.right, bottom: r.bottom })
const intersect = (a: Box, b: Box): Box => ({ left: Math.max(a.left, b.left), top: Math.max(a.top, b.top), right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom) })
const hasArea = (b: Box) => b.right > b.left && b.bottom > b.top
const contains = (b: Box, x: number, y: number) => x >= b.left && x <= b.right && y >= b.top && y <= b.bottom
/** A pointer's position; a synthesised event without coordinates (jsdom) reads as the origin. */
const point = (e: { clientX: number; clientY: number }) => ({ x: e.clientX || 0, y: e.clientY || 0 })

/** The nearest ancestor that scrolls vertically (the tenant's `main`), or null when the viewport does. */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
    for (let node = el?.parentElement ?? null; node && node !== document.body; node = node.parentElement) {
        const { overflowY } = getComputedStyle(node)
        if (overflowY === 'auto' || overflowY === 'scroll') { return node }
    }
    return null
}

/**
 * Snapshot every drop target's rect once per drag, topmost first: chips sit on the panel and the
 * panel floats over the headings, so the first zone containing the pointer is the one it sees. A
 * heading's rect is clipped to the frame, because the table scrolls sideways under it.
 */
function collectZones(root: HTMLElement, panel: HTMLElement | null): DropZone[] {
    const chips: DropZone[] = []
    const headings: DropZone[] = []
    let panelZone: DropZone | null = null
    const frame = toBox(root.getBoundingClientRect())
    for (const el of Array.from(root.querySelectorAll<HTMLElement>('[data-column-drop]'))) {
        const box = toBox(el.getBoundingClientRect())
        if (el.getAttribute('data-column-drop') === 'panel') { panelZone = { target: 'panel', axis: 'x', box }; continue }
        const index = Number(el.getAttribute('data-column-index'))
        if (!Number.isFinite(index)) { continue }
        if (panel?.contains(el)) { chips.push({ target: index, axis: 'y', box }) } else { headings.push({ target: index, axis: 'x', box: intersect(box, frame) }) }
    }
    return panelZone ? [...chips, panelZone, ...headings] : [...chips, ...headings]
}

/** The drop target under a pointer, from the cached zones; with no layout to measure (jsdom) the event's own target decides, as before. */
function resolveDrop(zones: DropZone[], event: { clientX: number; clientY: number; target: EventTarget | null }): DropTarget {
    if (zones.some(z => hasArea(z.box))) {
        const { x, y } = point(event)
        for (const zone of zones) {
            if (!hasArea(zone.box) || !contains(zone.box, x, y)) { continue }
            if (zone.target === 'panel') { return 'panel' }
            const past = zone.axis === 'x' ? x > (zone.box.left + zone.box.right) / 2 : y > (zone.box.top + zone.box.bottom) / 2
            return zone.target + (past ? 1 : 0)
        }
        return null
    }
    const hit = event.target instanceof Element ? event.target.closest('[data-column-drop]') : null
    if (!hit) { return null }
    if (hit.getAttribute('data-column-drop') === 'panel') { return 'panel' }
    const index = Number(hit.getAttribute('data-column-index'))
    return Number.isFinite(index) ? index : null
}

// Layout effects are for the browser; static markup (the @marque/ui tests) never opens the panel.
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export function useColumnConfigurator({ identity, layout, viewport, labelOf }: ColumnConfiguratorOptions): ColumnConfiguratorHandle {
    const { columns, allColumns, isCustomised, show, hide, move, reset } = layout
    const enabled = !!identity && allColumns.length >= 2
    const compact = viewport === 'compact'
    const [open, setOpen] = useState(false)
    const active = enabled && open
    const [drag, setDrag] = useState<DragSource | null>(null)
    const [drop, setDrop] = useState<DropTarget>(null)
    const [announcement, setAnnouncement] = useState('')
    const [positions, setPositions] = useState<Record<string, string>>({})
    const cogRef = useRef<HTMLButtonElement>(null)
    const frameRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLElement>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)
    const ghostRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<DragSource | null>(null)
    const dropRef = useRef<DropTarget>(null)
    const zones = useRef<DropZone[]>([])
    const pointer = useRef({ x: 0, y: 0 })
    const frameRequest = useRef<number | null>(null)
    const scrollParent = useRef<HTMLElement | null>(null)
    const pendingFocus = useRef<string | null>(null)

    const announce = useCallback((message: string) => setAnnouncement(message), [])
    const close = useCallback(() => {
        dragRef.current = null; dropRef.current = null
        setOpen(false); setDrag(null); setDrop(null)
        cogRef.current?.focus()
    }, [])

    // Focus follows the change: the chip that now holds the column the user just hid or showed.
    useEffect(() => {
        const selector = pendingFocus.current
        if (!selector || !panelRef.current) { return }
        pendingFocus.current = null
        panelRef.current.querySelector<HTMLElement>(selector)?.focus()
    })

    // AB#3988 — opening moves focus into the panel, so Escape and Tab start from it. The overlay is
    // on screen already, so no scrolling; the compact panel sits below the cards and may scroll in.
    useEffect(() => {
        if (active) { headingRef.current?.focus({ preventScroll: !compact }) }
    }, [active, compact])

    // AB#3986 — place the overlay: the frame's top-right, kept inside the frame's scroll parent (else
    // the viewport), its height bounded so it scrolls within itself. Written straight to the element:
    // scrolling and resizing must never re-render the table.
    const place = useCallback(() => {
        const frame = frameRef.current
        const panel = panelRef.current
        if (compact || !frame || !panel) { return }
        const viewport: Box = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
        const parent = scrollParent.current
        let bound = parent ? intersect(toBox(parent.getBoundingClientRect()), viewport) : viewport
        if (bound.bottom - bound.top < MIN_BOUND_HEIGHT || bound.right - bound.left < PANEL_WIDTH + 2 * GUTTER) { bound = viewport }
        const maxHeight = Math.max(MIN_PANEL_HEIGHT, Math.min(Math.round(viewport.bottom * 0.7), bound.bottom - bound.top - 2 * GUTTER))
        const width = Math.max(MIN_PANEL_WIDTH, Math.min(PANEL_WIDTH, viewport.right - 2 * GUTTER))
        panel.style.maxHeight = `${maxHeight}px`
        panel.style.width = `${width}px`
        const height = Math.min(panel.offsetHeight, maxHeight)
        const anchor = frame.getBoundingClientRect()
        const top = Math.max(bound.top + GUTTER, Math.min(anchor.top, bound.bottom - GUTTER - height))
        const left = Math.max(bound.left + GUTTER, Math.min(anchor.right - width, bound.right - GUTTER - width))
        panel.style.top = `${top}px`
        panel.style.left = `${left}px`
    }, [compact])

    useBrowserLayoutEffect(() => {
        if (!active || compact) { return }
        scrollParent.current = scrollParentOf(frameRef.current)
        place()
        document.addEventListener('scroll', place, true)
        window.addEventListener('resize', place)
        const panel = panelRef.current
        return () => {
            document.removeEventListener('scroll', place, true)
            window.removeEventListener('resize', place)
            scrollParent.current = null
            // The same element survives a tier change while open: leave it no inline geometry.
            if (panel) { panel.style.maxHeight = ''; panel.style.width = ''; panel.style.top = ''; panel.style.left = '' }
        }
    }, [active, compact, place])
    // The panel's own height changes as chips move between its lists: re-place after every render.
    useBrowserLayoutEffect(() => { if (active && !compact) { place() } })

    const doHide = useCallback((field: string) => {
        hide(field)
        announce(`${labelOf(field)} hidden`)
        pendingFocus.current = chipSelector(field, 'add')
    }, [hide, announce, labelOf])
    const doShow = useCallback((field: string, at: number | undefined) => {
        show(field, at)
        const position = at === undefined ? columns.length + 1 : Math.min(at, columns.length) + 1
        announce(`${labelOf(field)} shown as column ${position} of ${columns.length + 1}`)
        pendingFocus.current = chipSelector(field, 'hide')
    }, [show, announce, labelOf, columns.length])
    const doMove = useCallback((field: string, to: number) => {
        const clamped = Math.max(0, Math.min(to, columns.length - 1))
        move(field, clamped)
        announce(`${labelOf(field)} moved to position ${clamped + 1} of ${columns.length}`)
    }, [move, announce, labelOf, columns.length])
    const doReset = useCallback(() => {
        reset()
        announce('Columns reset to the default layout')
        pendingFocus.current = '[data-action="done"]'
    }, [reset, announce])

    // AB#3987 — the ghost follows the last pointer position, painted at most once per frame.
    const paintGhost = useCallback(() => {
        frameRequest.current = null
        const ghost = ghostRef.current
        if (ghost) { ghost.style.transform = `translate(${pointer.current.x + GHOST_OFFSET}px, ${pointer.current.y + GHOST_OFFSET}px)` }
    }, [])
    const endDrag = useCallback(() => {
        dragRef.current = null; dropRef.current = null
        setDrag(null); setDrop(null)
    }, [])
    const cancelDrag = useCallback(() => {
        if (!dragRef.current) { return }
        endDrag()
        announce('Drag cancelled')
    }, [endDrag, announce])
    const finishDrag = useCallback((target: DropTarget) => {
        const source = dragRef.current
        endDrag()
        if (!source || target === null) { return }
        if (target === 'panel') {
            if (source.from === 'visible') { doHide(source.field) }
            return
        }
        if (source.from === 'hidden') { doShow(source.field, target); return }
        const from = columns.findIndex(c => c.field === source.field)
        if (from < 0) { return }
        const to = target > from ? target - 1 : target
        if (to !== from) { doMove(source.field, to) }
    }, [columns, endDrag, doHide, doShow, doMove])

    // The ghost mounts at the grab point, before paint.
    useBrowserLayoutEffect(() => { if (drag) { paintGhost() } }, [drag, paintGhost])

    // The drop handlers close over the current columns (and a host's `labelOf` is usually a fresh
    // function per render), so the pointer subscription below reads them through a ref: it must live
    // for the whole drag, not re-subscribe on every marker change — its cleanup would cancel the
    // frame that moves the ghost and re-measure every target.
    const handlers = useRef({ finishDrag, cancelDrag })
    useBrowserLayoutEffect(() => { handlers.current = { finishDrag, cancelDrag } })

    // Window-level pointer tracking while a drag is live: no pointer capture needed, and the grip
    // that started the drag may be re-rendered away (a header moves) without ending it. Nothing here
    // renders per move: the ghost moves imperatively and the marker state only changes with its target.
    useEffect(() => {
        if (!drag) { return }
        const refresh = () => { zones.current = frameRef.current ? collectZones(frameRef.current, panelRef.current) : [] }
        const onMove = (e: PointerEvent) => {
            pointer.current = point(e)
            if (frameRequest.current === null) {
                if (typeof requestAnimationFrame === 'function') { frameRequest.current = requestAnimationFrame(paintGhost) } else { paintGhost() }
            }
            const next = resolveDrop(zones.current, e)
            if (next !== dropRef.current) { dropRef.current = next; setDrop(next) }
        }
        const onUp = (e: PointerEvent) => handlers.current.finishDrag(resolveDrop(zones.current, e))
        const onCancel = () => handlers.current.cancelDrag()
        refresh()
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        window.addEventListener('pointercancel', onCancel)
        window.addEventListener('blur', onCancel)
        document.addEventListener('scroll', refresh, true)
        window.addEventListener('resize', refresh)
        const body = document.body
        const restore = { cursor: body.style.cursor, userSelect: body.style.userSelect }
        body.style.cursor = 'grabbing'
        body.style.userSelect = 'none'
        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            window.removeEventListener('pointercancel', onCancel)
            window.removeEventListener('blur', onCancel)
            document.removeEventListener('scroll', refresh, true)
            window.removeEventListener('resize', refresh)
            if (frameRequest.current !== null && typeof cancelAnimationFrame === 'function') { cancelAnimationFrame(frameRequest.current) }
            frameRequest.current = null
            body.style.cursor = restore.cursor
            body.style.userSelect = restore.userSelect
        }
    }, [drag, paintGhost])

    const startDrag = useCallback((e: ReactPointerEvent<HTMLElement>, source: DragSource) => {
        // Only the primary button drags (a synthesised event with no button at all counts as primary).
        if (e.button > 0) { return }
        e.preventDefault()
        pointer.current = point(e)
        dragRef.current = source; dropRef.current = null
        setDrop(null)
        setDrag(source)
    }, [])

    // AB#3988 — Escape wherever focus is. Capture phase, so it runs before any React handler and the
    // stopPropagation keeps it from a parent dialog; a focused position <select> keeps its own Escape.
    useEffect(() => {
        if (!active) { return }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') { return }
            if (e.target instanceof HTMLSelectElement && panelRef.current?.contains(e.target)) { return }
            e.stopPropagation()
            if (dragRef.current) { cancelDrag(); return }
            close()
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => document.removeEventListener('keydown', onKeyDown, true)
    }, [active, cancelDrag, close])

    const grip = (field: string, from: DragSource['from']) => (
        <button type="button" data-action="grip" aria-label={`Drag ${labelOf(field)}`}
            title={from === 'visible' ? 'Drag to reorder, or drag into the panel to hide' : 'Drag onto the headings to show'}
            onPointerDown={e => startDrag(e, { field, from })} className={gripButton}>
            <span aria-hidden="true">⋮⋮</span>
        </button>
    )

    const header = (field: string, index: number): ColumnHeaderDecoration => {
        if (!active) { return IDLE_HEADER }
        const marker = drop === index ? 'before' : drop === columns.length && index === columns.length - 1 ? 'after' : undefined
        const lifted = drag?.from === 'visible' && drag.field === field
        const props: ColumnHeaderProps = { 'data-column-drop': 'header', 'data-column-index': index }
        if (marker) { props['data-column-marker'] = marker }
        if (lifted) { props['data-column-lifted'] = 'true' }
        const classes = [marker ? MARK[marker] : '', lifted ? 'opacity-50' : ''].filter(Boolean).join(' ')
        return { props, className: classes, grip: grip(field, 'visible') }
    }

    const cog = enabled ? (
        <button ref={cogRef} type="button" aria-label="Configure columns" aria-pressed={active} title="Configure columns"
            onClick={() => active ? close() : setOpen(true)}
            className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500${active ? ' bg-neutral-100 text-neutral-700 dark:bg-slate-700 dark:text-slate-200' : ''}`}>
            <Icon name="Settings" size={14} />
        </button>
    ) : null

    const hidden = allColumns.filter(c => !columns.some(v => v.field === c.field))
    const positionOf = (field: string): number | undefined => {
        const choice = positions[field] ?? END
        return choice === END ? undefined : Number(choice)
    }

    const frame = (table: ReactNode): ReactNode => {
        if (!active) { return table }
        // The table is rendered as given — no wrapper, no flex — so its box is exactly what it is idle;
        // the panel and the ghost are fixed, out of flow. (Compact: the panel stacks below, in flow.)
        return (
            <div ref={frameRef} data-column-configurator="active">
                {table}
                <ColumnPanel compact={compact} panelRef={panelRef} headingRef={headingRef} columns={columns} hidden={hidden} drop={drop} drag={drag} isCustomised={isCustomised}
                    labelOf={labelOf} grip={grip} positions={positions} positionOf={positionOf} announcement={announcement}
                    onPosition={(field, value) => setPositions(p => ({ ...p, [field]: value }))}
                    onShow={doShow} onHide={doHide} onMove={doMove} onReset={doReset} onDone={close} />
                {drag && (
                    <div ref={ghostRef} aria-hidden="true" data-column-ghost={drag.field}
                        className="pointer-events-none fixed left-0 top-0 z-[60] max-w-64 truncate rounded border border-blue-400 bg-white px-2 py-1 text-xs font-medium text-neutral-800 shadow-lg dark:border-blue-500 dark:bg-slate-800 dark:text-slate-100">
                        {labelOf(drag.field)}
                    </div>
                )}
            </div>
        )
    }

    return { enabled, active, cog, frame, header }
}

/**
 * The configuration panel. Exported for @marque/ui's static-markup tests (the interaction suite lives
 * in the designer's jsdom tests); hosts never render it directly — `frame` does.
 */
export function ColumnPanel({ compact, panelRef, headingRef, columns, hidden, drop, drag, isCustomised, labelOf, grip, positions, positionOf, announcement, onPosition, onShow, onHide, onMove, onReset, onDone }: {
    compact: boolean
    panelRef: RefObject<HTMLElement | null>
    headingRef: RefObject<HTMLHeadingElement | null>
    columns: TableColumn[]
    hidden: TableColumn[]
    drop: DropTarget
    drag: DragSource | null
    isCustomised: boolean
    labelOf: (field: string) => string
    grip: (field: string, from: DragSource['from']) => ReactNode
    positions: Record<string, string>
    positionOf: (field: string) => number | undefined
    announcement: string
    onPosition: (field: string, value: string) => void
    onShow: (field: string, at: number | undefined) => void
    onHide: (field: string) => void
    onMove: (field: string, to: number) => void
    onReset: () => void
    onDone: () => void
}) {
    const overPanel = drop === 'panel' && drag?.from === 'visible'
    // Wide: a fixed overlay (placed by the hook), bounded and scrolling within itself, above the
    // table's sticky chrome. Compact: in flow below the cards, unbounded — one scroll region, the page.
    const placement = compact
        ? 'border-t'
        : 'fixed z-50 w-72 overflow-y-auto overscroll-contain rounded-md border shadow-lg'
    return (
        <section ref={panelRef} role="region" aria-label="Configure columns" data-column-drop="panel"
            className={`text-xs text-neutral-700 dark:text-slate-200 bg-neutral-50 dark:bg-slate-900 border-neutral-200 dark:border-slate-700 ${placement}${overPanel ? ' ring-2 ring-inset ring-blue-500' : ''}`}>
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-2 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-900">
                <h4 ref={headingRef} tabIndex={-1} className="text-sm font-semibold text-neutral-800 dark:text-slate-100 focus:outline-none">Columns</h4>
                <button type="button" data-action="done" onClick={onDone} className={smallButton}>Done</button>
            </div>
            <p className="px-3 pt-2 text-[11px] text-neutral-500 dark:text-slate-400">
                Drag a heading to reorder it, or drag it into this panel to hide it. Drag an available column onto the headings to show it — or use the buttons.
            </p>
            <div className="px-3 pt-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-slate-500">Shown</div>
                <ul className="mt-1 space-y-1">
                    {columns.map((c, i) => {
                        const label = labelOf(c.field)
                        const marker = drop === i ? 'above' : drop === columns.length && i === columns.length - 1 ? 'below' : undefined
                        const lifted = drag?.from === 'visible' && drag.field === c.field
                        const classes = [
                            'flex items-center gap-1 rounded border border-neutral-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1.5 py-1',
                            marker ? MARK[marker] : '',
                            lifted ? 'opacity-50' : '',
                        ].filter(Boolean).join(' ')
                        return (
                            <li key={c.field} data-column-chip={c.field} data-column-drop="header" data-column-index={i}
                                {...(marker ? { 'data-column-marker': marker } : {})} {...(lifted ? { 'data-column-lifted': 'true' } : {})} className={classes}>
                                {grip(c.field, 'visible')}
                                <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
                                <button type="button" data-action="move-left" aria-label={`Move ${label} left`} disabled={i === 0}
                                    onClick={() => onMove(c.field, i - 1)} className={smallButton}>{compact ? 'Up' : 'Left'}</button>
                                <button type="button" data-action="move-right" aria-label={`Move ${label} right`} disabled={i === columns.length - 1}
                                    onClick={() => onMove(c.field, i + 1)} className={smallButton}>{compact ? 'Down' : 'Right'}</button>
                                <button type="button" data-action="hide" aria-label={`Hide ${label}`} disabled={columns.length <= 1}
                                    onClick={() => onHide(c.field)} className={smallButton}>Hide</button>
                            </li>
                        )
                    })}
                </ul>
            </div>
            <div className="px-3 pt-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-slate-500">Available</div>
                {hidden.length === 0
                    ? <p className="mt-1 italic text-neutral-400 dark:text-slate-500">All columns are shown.</p>
                    : <ul className="mt-1 space-y-1">
                        {hidden.map(c => {
                            const label = labelOf(c.field)
                            return (
                                <li key={c.field} data-column-chip={c.field}
                                    className={`flex items-center gap-1 rounded border border-dashed border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-1${drag?.from === 'hidden' && drag.field === c.field ? ' opacity-50' : ''}`}>
                                    {grip(c.field, 'hidden')}
                                    <span className="min-w-0 flex-1 truncate" title={label}>{label}</span>
                                    <select aria-label={`Position for ${label}`} value={positions[c.field] ?? END} onChange={e => onPosition(c.field, e.target.value)}
                                        className="marque-input rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 py-0.5 text-[11px] max-w-[7rem]">
                                        <option value={END}>At the end</option>
                                        {columns.map((v, i) => <option key={v.field} value={String(i)}>Before {labelOf(v.field)}</option>)}
                                    </select>
                                    <button type="button" data-action="add" aria-label={`Add ${label}`} onClick={() => onShow(c.field, positionOf(c.field))} className={smallButton}>Add</button>
                                </li>
                            )
                        })}
                    </ul>}
            </div>
            <div className="flex items-center justify-between gap-2 px-3 py-3">
                <button type="button" data-action="reset" onClick={onReset} disabled={!isCustomised} className={smallButton}>Reset to default</button>
                <span className="text-[11px] text-neutral-400 dark:text-slate-500">Esc closes</span>
            </div>
            <div aria-live="polite" className="sr-only">{announcement}</div>
        </section>
    )
}
